import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { 
  CheckCircle, Clock, Eye, CheckSquare, PackageCheck, XCircle, 
  User, Store, SendHorizontal, DollarSign, Star, Send, 
  Banknote, TicketCheck, MessageSquare, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '../contexts/LanguageContext';

// Star Rating Component
const StarRating = ({ rating, setRating, readonly = false, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && setRating(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          className={`transition-all duration-150 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              (hoverRating || rating) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-300'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

// Inline Rating Form Component
const InlineRatingForm = ({ transaction, user, onRatingSubmitted }) => {
  const { language } = useLanguage();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [satisfaction, setSatisfaction] = useState({
    communication: 0,
    speed: 0,
    reliability: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [loading, setLoading] = useState(true);

  const translations = {
    fr: {
      rateExperience: 'Évaluez votre expérience',
      withSeller: 'avec le vendeur',
      withBuyer: "avec l'acheteur",
      overallRating: 'Note globale',
      communication: 'Communication',
      speed: 'Rapidité',
      reliability: 'Fiabilité',
      comment: 'Commentaire (optionnel)',
      commentPlaceholder: 'Partagez votre expérience...',
      submit: 'Soumettre',
      submitting: 'Envoi...',
      thankYou: 'Merci pour votre évaluation!',
      alreadyRated: 'Vous avez évalué ce transfert',
      selectRating: 'Veuillez sélectionner une note'
    },
    en: {
      rateExperience: 'Rate your experience',
      withSeller: 'with the seller',
      withBuyer: 'with the buyer',
      overallRating: 'Overall rating',
      communication: 'Communication',
      speed: 'Speed',
      reliability: 'Reliability',
      comment: 'Comment (optional)',
      commentPlaceholder: 'Share your experience...',
      submit: 'Submit',
      submitting: 'Submitting...',
      thankYou: 'Thank you for your rating!',
      alreadyRated: 'You have rated this transfer',
      selectRating: 'Please select a rating'
    }
  };

  const t = translations[language] || translations.fr;
  const isBuyer = transaction.buyer_email === user?.email || transaction.buyer_client_number === user?.client_number;

  useEffect(() => {
    checkRatingStatus();
  }, [transaction.id]);

  const checkRatingStatus = async () => {
    try {
      const response = await axios.get(`${API}/ratings/transaction/${transaction.id}`);
      // The API returns an array of ratings - check if current user has already rated
      const ratings = response.data || [];
      const userHasRated = ratings.some(r => r.rater_email === user?.email);
      setHasRated(userHasRated);
    } catch (error) {
      console.error('Error checking rating status:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error(t.selectRating);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/ratings`, {
        transaction_id: transaction.id,
        rating,
        comment: comment.trim() || null,
        satisfaction_survey: satisfaction
      });
      
      toast.success(t.thankYou);
      setHasRated(true);
      if (onRatingSubmitted) {
        onRatingSubmitted();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (hasRated) {
    return (
      <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg p-3 mt-2">
        <CheckCircle className="h-4 w-4" />
        <span className="text-sm font-medium">{t.alreadyRated}</span>
      </div>
    );
  }

  return (
    <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
      <p className="text-sm font-medium text-gray-800">
        {t.rateExperience} {isBuyer ? t.withSeller : t.withBuyer}
      </p>
      
      {/* Prize draw message */}
      <div className="bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-300 rounded-lg p-3">
        <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
          {language === 'en' 
            ? 'Complete a testimonial visible on the site and enter for a chance to win a $50 Middleman credit in the weekly draw!' 
            : 'Complétez un témoignage visible sur le site et courez la chance de gagner un crédit Middleman de 50$ lors du tirage de la semaine!'}
        </p>
      </div>
      
      {/* Overall Rating */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-600">{t.overallRating}:</span>
        <StarRating rating={rating} setRating={setRating} size="md" />
      </div>

      {/* Satisfaction Survey - Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-2">
        <div className="flex sm:flex-col items-center sm:text-center justify-between sm:justify-start">
          <span className="text-xs text-gray-600 sm:block sm:mb-1">{t.communication}</span>
          <StarRating 
            rating={satisfaction.communication} 
            setRating={(v) => setSatisfaction(prev => ({...prev, communication: v}))} 
            size="sm" 
          />
        </div>
        <div className="flex sm:flex-col items-center sm:text-center justify-between sm:justify-start">
          <span className="text-xs text-gray-600 sm:block sm:mb-1">{t.speed}</span>
          <StarRating 
            rating={satisfaction.speed} 
            setRating={(v) => setSatisfaction(prev => ({...prev, speed: v}))} 
            size="sm" 
          />
        </div>
        <div className="flex sm:flex-col items-center sm:text-center justify-between sm:justify-start">
          <span className="text-xs text-gray-600 sm:block sm:mb-1">{t.reliability}</span>
          <StarRating 
            rating={satisfaction.reliability} 
            setRating={(v) => setSatisfaction(prev => ({...prev, reliability: v}))} 
            size="sm" 
          />
        </div>
      </div>

      {/* Comment */}
      <div>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t.commentPlaceholder}
          className="text-sm min-h-[60px]"
        />
      </div>

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        disabled={submitting || rating === 0}
        size="sm"
        className="w-full bg-brand-600 hover:bg-brand-700"
      >
        {submitting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {t.submitting}
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            {t.submit}
          </>
        )}
      </Button>
    </div>
  );
};

const TransactionTimeline = ({ transaction, onPaymentClick, onConfirmClick, confirmingTransaction, currentUserEmail, user, onRatingSubmitted }) => {
  const { language } = useLanguage();
  const [platformsMap, setPlatformsMap] = useState({});
  
  // Fetch platforms from API
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const response = await axios.get(`${API}/platforms`);
        const map = {};
        response.data.forEach(p => {
          map[p.name] = {
            name: p.display_name || p.name,
            loginUrl: p.login_url
          };
        });
        setPlatformsMap(map);
      } catch (error) {
        console.error('Error fetching platforms:', error);
      }
    };
    fetchPlatforms();
  }, []);
  
  const translations = {
    fr: {
      created: 'Demande de transfert créée',
      createdDesc: 'Demande de transfert initiée',
      buyerConfirmation: 'Confirmation acheteur',
      buyerConfirmationDesc: "L'acheteur a confirmé le transfert",
      buyerConfirmationPending: "En attente de votre confirmation",
      sellerConfirmation: 'Confirmation vendeur',
      sellerConfirmationDesc: 'Le vendeur a confirmé le transfert',
      sellerConfirmationPending: "En attente de votre confirmation",
      confirmNow: 'Confirmer maintenant',
      confirming: 'Confirmation...',
      buyerPayment: 'Paiement acheteur',
      buyerPaymentDesc: 'Paiement des frais et du montant du transfert',
      payNow: 'Payer maintenant',
      ticketsSent: 'Billets transférés',
      ticketsSentDesc: 'Le vendeur a transféré les billets via la plateforme de billetterie',
      ocrVerification: 'Vérification OCR',
      ocrVerificationDesc: 'Vérification automatique complétée',
      adminVerification: 'Billets vérifiés',
      adminVerificationDesc: 'Middleman a reçu et vérifié les billets',
      fundsReleased: 'Fonds envoyés au vendeur',
      fundsReleasedDesc: 'Le vendeur a reçu le paiement',
      ticketsTransferred: 'Billets transférés à l\'acheteur',
      ticketsTransferredDesc: "L'acheteur a reçu les billets vérifiés",
      buyerRating: 'Évaluation acheteur',
      buyerRatingDesc: "L'acheteur évalue le vendeur",
      sellerRating: 'Évaluation vendeur',
      sellerRatingDesc: "Le vendeur évalue l'acheteur",
      completed: 'Complétée',
      completedDesc: 'Transfert terminé avec succès',
      disputed: 'Litige',
      disputedDesc: 'Ce transfert nécessite une intervention administrative',
      waiting: 'En attente',
      waitingBoth: 'Les deux parties doivent confirmer',
      waitingSeller: 'En attente de confirmation du vendeur',
      waitingBuyer: "En attente de confirmation de l'acheteur",
      lastUpdate: 'Dernière mise à jour'
    },
    en: {
      created: 'Transfer request created',
      createdDesc: 'Transfer request initiated',
      buyerConfirmation: 'Buyer confirmation',
      buyerConfirmationDesc: 'Buyer has confirmed the transfer',
      buyerConfirmationPending: 'Waiting for your confirmation',
      sellerConfirmation: 'Seller confirmation',
      sellerConfirmationDesc: 'Seller has confirmed the transfer',
      sellerConfirmationPending: 'Waiting for your confirmation',
      confirmNow: 'Confirm now',
      confirming: 'Confirming...',
      buyerPayment: 'Buyer payment',
      buyerPaymentDesc: 'Payment of fees and transfer amount',
      payNow: 'Pay now',
      ticketsSent: 'Tickets transferred',
      ticketsSentDesc: 'Seller transferred tickets via their ticketing platform',
      ocrVerification: 'OCR verification',
      ocrVerificationDesc: 'Automatic verification completed',
      adminVerification: 'Tickets verified',
      adminVerificationDesc: 'Middleman received and verified tickets',
      fundsReleased: 'Funds sent to seller',
      fundsReleasedDesc: 'Seller received payment',
      ticketsTransferred: 'Tickets transferred to buyer',
      ticketsTransferredDesc: 'Buyer received verified tickets',
      buyerRating: 'Buyer rating',
      buyerRatingDesc: 'Buyer rates the seller',
      sellerRating: 'Seller rating',
      sellerRatingDesc: 'Seller rates the buyer',
      completed: 'Completed',
      completedDesc: 'Transfer completed successfully',
      disputed: 'Dispute',
      disputedDesc: 'This transfer requires administrative intervention',
      waiting: 'Waiting',
      waitingBoth: 'Both parties must confirm',
      waitingSeller: 'Waiting for seller confirmation',
      waitingBuyer: 'Waiting for buyer confirmation',
      lastUpdate: 'Last update'
    }
  };

  const t = translations[language] || translations.fr;
  const isBuyer = currentUserEmail === transaction.buyer_email || user?.client_number === transaction.buyer_client_number;
  const isSeller = currentUserEmail === transaction.seller_email || user?.client_number === transaction.seller_client_number;

  // Define all possible transaction statuses with their details
  // Both parties can progress simultaneously
  const timelineSteps = [
    {
      key: 'created',
      label: t.created,
      icon: CheckCircle,
      description: t.createdDesc,
      checkStatus: () => true,
      detailedInstructions: null
    },
    {
      key: 'buyer_confirmation',
      label: t.buyerConfirmation,
      icon: User,
      description: transaction.buyer_confirmed ? t.buyerConfirmationDesc : (isBuyer ? t.buyerConfirmationPending : t.buyerConfirmationDesc),
      checkStatus: () => transaction.buyer_confirmed,
      requiresConfirmation: true,
      showConfirmFor: 'buyer',
      detailedInstructions: isBuyer ? "Vérifiez les détails du transfert (événement, nombre de billets, prix) et confirmez votre accord." : null
    },
    {
      key: 'seller_confirmation',
      label: t.sellerConfirmation,
      icon: Store,
      description: transaction.seller_confirmed ? t.sellerConfirmationDesc : (isSeller ? t.sellerConfirmationPending : t.sellerConfirmationDesc),
      checkStatus: () => transaction.seller_confirmed,
      requiresConfirmation: true,
      showConfirmFor: 'seller',
      detailedInstructions: isSeller ? "Vérifiez les détails du transfert (événement, nombre de billets, prix) et confirmez votre accord." : null
    },
    {
      key: 'payment_buyer',
      label: t.buyerPayment,
      icon: DollarSign,
      description: t.buyerPaymentDesc,
      checkStatus: () => {
        // Check multiple indicators that payment was completed
        if (transaction.payment_completed) return true;
        if (transaction.payment_completed_at) return true;
        // If later steps are done, payment must have been completed
        if (transaction.tickets_verified) return true;
        if (transaction.funds_released) return true;
        if (transaction.tickets_transferred_to_buyer) return true;
        // Check status progression
        const paidStatuses = ['payment_completed', 'payment_received', 'tickets_uploaded', 'tickets_verified', 'ocr_verified', 'admin_verified', 'funds_released', 'tickets_transferred', 'completed'];
        return paidStatuses.includes(transaction.status);
      },
      requiresAction: true,
      actionLabel: t.payNow,
      showForBuyer: true,
      detailedInstructions: isBuyer ? "Effectuez le paiement du montant établi + les frais de sécurisation (6,95$ à 19,95$ selon le montant total du transfert). Middleman conservera le montant jusqu'à la vérification des billets." : null
    },
    {
      key: 'tickets_sent',
      label: t.ticketsSent,
      icon: SendHorizontal,
      description: t.ticketsSentDesc,
      checkStatus: () => ['tickets_uploaded', 'tickets_verified', 'ocr_verified', 'admin_verified', 'funds_released', 'tickets_transferred', 'completed'].includes(transaction.status) || transaction.tickets_verified,
      detailedInstructions: isSeller ? `Connectez-vous à ${transaction.ticket_platform || 'votre plateforme'}, sélectionnez vos billets et transférez-les à : info@transfertdebillets.ca` : null,
      showPlatformLogin: isSeller
    },
    {
      key: 'admin_verified',
      label: t.adminVerification,
      icon: Eye,
      description: t.adminVerificationDesc,
      checkStatus: () => ['tickets_verified', 'admin_verified', 'funds_released', 'tickets_transferred', 'completed'].includes(transaction.status) || transaction.tickets_verified,
      detailedInstructions: "Notre équipe vérifie que les billets correspondent aux informations de la demande via la plateforme officielle."
    },
    {
      key: 'funds_released',
      label: t.fundsReleased,
      icon: Banknote,
      description: t.fundsReleasedDesc,
      checkStatus: () => transaction.funds_released || ['funds_released', 'tickets_transferred', 'completed'].includes(transaction.status),
      detailedInstructions: isSeller ? "Une fois les billets vérifiés, le paiement vous est envoyé via votre méthode préférée (Interac ou Wise)." : null
    },
    {
      key: 'tickets_transferred',
      label: t.ticketsTransferred,
      icon: TicketCheck,
      description: t.ticketsTransferredDesc,
      checkStatus: () => ['tickets_transferred', 'completed'].includes(transaction.status) || transaction.tickets_transferred,
      detailedInstructions: isBuyer ? "Vous recevrez un courriel de la plateforme de billetterie pour accepter le transfert des billets vérifiés." : null
    },
    {
      key: 'buyer_rating',
      label: t.buyerRating,
      icon: Star,
      description: t.buyerRatingDesc,
      checkStatus: () => transaction.buyer_rated,
      showRatingForm: isBuyer,
      hideFromOther: true,
      detailedInstructions: isBuyer ? "Partagez votre expérience avec le vendeur pour aider la communauté." : null
    },
    {
      key: 'seller_rating',
      label: t.sellerRating,
      icon: Star,
      description: t.sellerRatingDesc,
      checkStatus: () => transaction.seller_rated,
      showRatingForm: isSeller,
      hideFromOther: true,
      detailedInstructions: isSeller ? "Partagez votre expérience avec l'acheteur pour aider la communauté." : null
    },
    {
      key: 'completed',
      label: t.completed,
      icon: PackageCheck,
      description: t.completedDesc,
      checkStatus: () => transaction.status === 'completed' || (transaction.buyer_rated && transaction.seller_rated),
      detailedInstructions: null
    }
  ];

  const isDisputed = transaction.status === 'disputed';

  // Filter steps based on user role (hide other party's rating)
  const filteredSteps = timelineSteps.filter(step => {
    if (step.key === 'buyer_rating' && isSeller) return false; // Hide buyer rating from seller
    if (step.key === 'seller_rating' && isBuyer) return false; // Hide seller rating from buyer
    return true;
  });

  // Determine which steps are completed based on custom logic
  const getStepStatus = (step, index) => {
    if (isDisputed && index > 2) return { isCompleted: false, isCurrent: false };
    
    const isCompleted = step.checkStatus();
    
    // Determine if this is the current step (first incomplete step)
    let isCurrent = false;
    if (!isCompleted && index > 0) {
      const prevStep = filteredSteps[index - 1];
      if (prevStep && prevStep.checkStatus()) {
        isCurrent = true;
      }
    }
    
    return { isCompleted, isCurrent };
  };

  // Check if rating step should show form
  const canShowRatingForm = (step) => {
    if (!step.showRatingForm) return false;
    // Only show rating form after payment completed or transaction verified
    const validStatuses = ['payment_completed', 'payment_received', 'admin_verified', 'funds_released', 'tickets_transferred', 'completed'];
    return validStatuses.includes(transaction.status) || 
           transaction.payment_completed || 
           transaction.payment_completed_at ||
           transaction.tickets_verified ||
           transaction.funds_released;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-CA', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="w-full py-4">
      <div className="relative max-w-2xl mx-auto">
        {/* Timeline Steps - Vertical Layout */}
        <div className="relative space-y-6">
          {filteredSteps.map((step, index) => {
            const { isCompleted, isCurrent } = getStepStatus(step, index);
            const Icon = step.icon;
            const isLast = index === filteredSteps.length - 1;
            const isRatingStep = step.key === 'buyer_rating' || step.key === 'seller_rating';

            return (
              <div key={step.key} className="relative flex items-start">
                {/* Vertical Line */}
                {!isLast && (
                  <div 
                    className="absolute left-6 top-12 w-0.5 h-full"
                    style={{
                      backgroundColor: isCompleted ? '#10b981' : '#e5e7eb',
                      zIndex: 0
                    }}
                  />
                )}

                {/* Circle with Icon */}
                <div className="relative z-10 flex-shrink-0">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center
                    transition-all duration-300 relative
                    ${isCompleted 
                      ? isRatingStep ? 'bg-yellow-500 text-white shadow-lg' : 'bg-brand-600 text-white shadow-lg'
                      : 'bg-white border-2 border-gray-300 text-gray-400'
                    }
                    ${isCurrent ? 'ring-4 ring-brand-200 scale-110' : ''}
                  `}>
                    <Icon className="w-6 h-6" />
                    {isCompleted && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-brand-600" fill="currentColor" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="ml-4 flex-1 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className={`text-sm sm:text-base font-semibold ${isCompleted || isCurrent ? 'text-gray-900' : 'text-gray-500'}`}>
                        {step.label}
                      </p>
                      <p className={`text-xs sm:text-sm mt-1 ${isCompleted || isCurrent ? 'text-gray-600' : 'text-gray-400'}`}>
                        {step.description}
                      </p>
                      
                      {/* Detailed Instructions - Show for current or actionable steps */}
                      {step.detailedInstructions && (isCurrent || (!isCompleted && (
                        (step.showConfirmFor === 'buyer' && isBuyer && !transaction.buyer_confirmed) ||
                        (step.showConfirmFor === 'seller' && isSeller && !transaction.seller_confirmed) ||
                        (step.key === 'tickets_sent' && isSeller && transaction.seller_confirmed && !transaction.tickets_verified) ||
                        (step.key === 'payment_buyer' && isBuyer && transaction.buyer_confirmed && transaction.seller_confirmed && !transaction.payment_completed)
                      ))) && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs sm:text-sm text-blue-800">
                            <span className="font-semibold">💡 Instructions : </span>
                            {step.detailedInstructions}
                          </p>
                        </div>
                      )}
                      
                      {/* Platform Login Button - Show for seller on tickets_sent step */}
                      {step.showPlatformLogin && !isCompleted && transaction.ticket_platform && platformsMap[transaction.ticket_platform] && (
                        <a
                          href={platformsMap[transaction.ticket_platform].loginUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 text-sm font-semibold rounded-lg shadow-md transition-colors duration-200"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Se connecter à {platformsMap[transaction.ticket_platform].name}
                        </a>
                      )}
                      
                      {/* Confirmation Button for buyer/seller confirmation steps */}
                      {step.requiresConfirmation && !isCompleted && onConfirmClick && (
                        (step.showConfirmFor === 'buyer' && isBuyer) || 
                        (step.showConfirmFor === 'seller' && isSeller)
                      ) && (
                        <button
                          onClick={onConfirmClick}
                          disabled={confirmingTransaction}
                          className="mt-3 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white text-sm font-medium rounded-lg shadow-md transition-colors duration-200 flex items-center"
                        >
                          {confirmingTransaction ? (
                            <>
                              <Clock className="w-4 h-4 mr-2 animate-spin" />
                              {t.confirming}
                            </>
                          ) : (
                            <>
                              <CheckSquare className="w-4 h-4 mr-2" />
                              {t.confirmNow}
                            </>
                          )}
                        </button>
                      )}

                      {/* Action Button for payment step */}
                      {step.requiresAction && !isCompleted && isBuyer && onPaymentClick && (
                        // Show payment button if buyer/seller confirmed and payment not completed
                        (step.key === 'payment_buyer' && transaction.buyer_confirmed && transaction.seller_confirmed) && (
                        <button
                          onClick={onPaymentClick}
                          data-testid="payment-button"
                          className="mt-3 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg shadow-md transition-colors duration-200 flex items-center"
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          {step.actionLabel}
                        </button>
                        )
                      )}

                      {/* Rating Form - Inline in timeline */}
                      {canShowRatingForm(step) && !isCompleted && user && (
                        <InlineRatingForm 
                          transaction={transaction}
                          user={user}
                          onRatingSubmitted={onRatingSubmitted}
                        />
                      )}
                    </div>
                    
                    {/* Date */}
                    {isCurrent && transaction.updated_at && (
                      <div className="ml-4 flex-shrink-0">
                        <p className="text-xs sm:text-sm text-brand-600 font-semibold whitespace-nowrap">
                          {formatDate(transaction.updated_at)}
                        </p>
                      </div>
                    )}
                    {isCompleted && transaction.created_at && index === 0 && (
                      <div className="ml-4 flex-shrink-0">
                        <p className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                          {formatDate(transaction.created_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Disputed Status Overlay */}
        {isDisputed && (
          <div className="mt-8 bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-center">
            <XCircle className="w-8 h-8 text-red-600 mr-3 flex-shrink-0" />
            <div>
              <p className="text-red-800 font-semibold">{t.disputed}</p>
              <p className="text-red-700 text-sm mt-1">{t.disputedDesc}</p>
              {transaction.updated_at && (
                <p className="text-red-600 text-xs mt-2">
                  {t.lastUpdate} : {formatDate(transaction.updated_at)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Info supplémentaire si toutes les confirmations ne sont pas faites */}
        {(!transaction.buyer_confirmed || !transaction.seller_confirmed) && !isDisputed && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">{t.waiting} : </span>
              {!transaction.buyer_confirmed && !transaction.seller_confirmed && t.waitingBoth}
              {transaction.buyer_confirmed && !transaction.seller_confirmed && t.waitingSeller}
              {!transaction.buyer_confirmed && transaction.seller_confirmed && t.waitingBuyer}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionTimeline;
